import { Dispatch, SetStateAction } from "react";
import { Capability } from "./prompt-types";
import type { PromptOptions } from "@/components/playground/prompt-caption";
import { InferenceType, SegmentResult } from "@/lib/playground-utils";

const STREAM_ERROR_MESSAGE =
  "Unable to stream segmentation result. Please try again.";

// Remote md-api endpoint (the base URL should already include /v1)
const SEGMENTATION_ENDPOINT = "/segment";

const INITIAL_SEGMENT_MESSAGE = "Preparing segmentation stream...";

type StreamSegmentationInferenceArgs = {
  inferenceUrl: string;
  query: string;
  imageFile: File;
  uploadedImage: string;
  promptOptions: PromptOptions;
  reasoningEnabled: boolean;
  variant?: string;
  setInferences: Dispatch<SetStateAction<InferenceType[]>>;
  capability?: Capability;
};

// md-api streams Server-Sent Events; see ../md-api/src/endpoints/segment.ts

type SegmentationStreamAccumulator = {
  bbox: { x_min: number; y_min: number; x_max: number; y_max: number } | null;
  path: string;
};

type BBox = { x_min: number; y_min: number; x_max: number; y_max: number };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isBBox(value: unknown): value is BBox {
  if (!isRecord(value)) return false;
  return ["x_min", "y_min", "x_max", "y_max"].every((k) => typeof value[k] === "number");
}

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

const createAccumulator = (): SegmentationStreamAccumulator => ({
  bbox: null,
  path: "",
});

const accumulatorToResult = (
  accumulator: SegmentationStreamAccumulator,
): SegmentResult | null => {
  if (!accumulator.bbox) return null;
  return {
    bbox: accumulator.bbox,
    path: accumulator.path.trim(),
  };
};

const appendCommands = (existing: string, incoming: string) =>
  existing ? `${existing}${incoming}` : incoming;

const formatSegmentSnapshot = (snapshot: SegmentResult) =>
  JSON.stringify(snapshot, null, 2);

const updateInferenceById =
  (
    setInferences: Dispatch<SetStateAction<InferenceType[]>>,
    inferenceId: number,
  ) =>
    (update: Partial<InferenceType>) => {
      setInferences((prev) =>
        prev.map((item) =>
          item.id === inferenceId ? { ...item, ...update } : item,
        ),
      );
    };

export async function streamSegmentationInference(
  args: StreamSegmentationInferenceArgs,
) {
  const { inferenceUrl, query, imageFile, uploadedImage, variant, setInferences, capability } =
    args;
  const effectiveCapability = capability ?? Capability.Segmentation;
  const inferenceId = Date.now();
  const applyUpdate = updateInferenceById(setInferences, inferenceId);

  setInferences((prev) => [
    {
      id: inferenceId,
      image: uploadedImage,
      imageFile,
      result: INITIAL_SEGMENT_MESSAGE,
      capability: effectiveCapability,
      query,
      status: "pending",
    },
    ...prev,
  ]);

  try {
    const base64Image = await fileToBase64(imageFile);
    const requestBody: Record<string, unknown> = {
      image_url: base64Image,
      object: query,
      stream: true,
    };

    if (variant) {
      requestBody.variant = variant;
    }

    const requestBodyString = JSON.stringify(requestBody);
    const requestInit: RequestInit = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: requestBodyString,
    };

    // Prefer real md-api endpoint first; keep local mock as a fallback
    const candidateEndpoints = [
      `${inferenceUrl}${SEGMENTATION_ENDPOINT}`,
    ];

    let response: Response | null = null;
    let lastError: Error | null = null;

    for (const endpoint of candidateEndpoints) {
      try {
        const attempt = await fetch(endpoint, { ...requestInit });
        if (attempt.ok) {
          response = attempt;
          break;
        }
        lastError = new Error(`API error: ${attempt.status} ${attempt.statusText}`);
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
      }
    }

    if (!response) {
      throw lastError ?? new Error("Segmentation endpoint unavailable.");
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("Streaming is not supported in this environment.");
    }

    const startTime = performance.now();
    const decoder = new TextDecoder();
    let buffer = "";
    const accumulator = createAccumulator();
    let lastSnapshot: SegmentResult | null = null;
    let streamCompleted = false;

    const emitSnapshot = (status: InferenceType["status"]) => {
      const snapshot = accumulatorToResult(accumulator);
      if (snapshot) {
        lastSnapshot = snapshot;
        applyUpdate({
          result: formatSegmentSnapshot(snapshot),
          segmentResult: snapshot,
          status,
        });
      } else {
        applyUpdate({ status });
      }
    };

    const finalize = () => {
      const finalSnapshot = accumulatorToResult(accumulator) ?? lastSnapshot;
      if (!finalSnapshot) {
        throw new Error("Segmentation stream completed without data.");
      }

      lastSnapshot = finalSnapshot;
      applyUpdate({
        result: formatSegmentSnapshot(finalSnapshot),
        segmentResult: finalSnapshot,
        status: "done",
        duration: performance.now() - startTime,
      });
      streamCompleted = true;
    };

    // Parse md-api SSE stream for segmentation
    const processSSELine = (line: string) => {
      const trimmed = line.trim();
      if (trimmed === "" || trimmed.startsWith(":")) return; // comments/keepalive
      if (!trimmed.startsWith("data:")) return; // only care about data lines
      const payload = trimmed.slice(5).trim();
      if (payload === "" || payload === "[DONE]") return;

      let obj: unknown;
      try {
        obj = JSON.parse(payload) as unknown;
      } catch (err) {
        console.error("Failed to parse segmentation SSE payload", err);
        return;
      }

      if (!isRecord(obj)) return;
      const type = typeof obj.type === "string" ? (obj.type as string) : undefined;
      if (type === "bbox" && isBBox(obj.bbox)) {
        const b = obj.bbox as BBox;
        accumulator.bbox = {
          x_min: clamp01(b.x_min),
          y_min: clamp01(b.y_min),
          x_max: clamp01(b.x_max),
          y_max: clamp01(b.y_max),
        };
        emitSnapshot("streaming");
        return;
      }

      if (type === "path_delta") {
        const chunk = typeof obj.chunk === "string" ? obj.chunk : "";
        if (chunk) {
          accumulator.path = appendCommands(accumulator.path, chunk);
          emitSnapshot("streaming");
        }
        return;
      }

      if (type === "final") {
        const finalPath = typeof obj.path === "string" ? (obj.path as string) : accumulator.path;
        if (finalPath) accumulator.path = finalPath;
        const b = isBBox(obj.bbox) ? (obj.bbox as BBox) : undefined;
        if (b) {
          accumulator.bbox = {
            x_min: clamp01(b.x_min),
            y_min: clamp01(b.y_min),
            x_max: clamp01(b.x_max),
            y_max: clamp01(b.y_max),
          };
        }
        finalize();
        return;
      }
    };

    while (true) {
      const { value, done } = await reader.read();
      if (done) {
        buffer += decoder.decode();
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      let newlineIndex: number;
      while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
        const line = buffer.slice(0, newlineIndex);
        buffer = buffer.slice(newlineIndex + 1);
        processSSELine(line);
      }
    }

    if (buffer.trim().length > 0) {
      processSSELine(buffer);
    }

    if (!streamCompleted) {
      finalize();
    }
  } catch (error) {
    console.error("Segmentation streaming error", error);
    applyUpdate({ result: STREAM_ERROR_MESSAGE, status: "error" });
    throw error;
  }
}

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (event) => reject(event);
  });
